import { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Can } from '../Can';

import {
  Box,
  IconButton,
  Menu,
  Tooltip,
  MenuItem,
  useTheme,
} from '@mui/material';

import {
  ReplayCircleFilled,
  Cancel,
  CheckCircle,
  MoreVert,
  Replay,
  SwapHorizontalCircle,
} from '@mui/icons-material';

import { green, grey, orange, red } from '@mui/material/colors';

import ButtonWithSpinner from '../ButtonWithSpinner';
import ConfirmationModal from '../ConfirmationModal';
import TransferTicketModalCustom from '../TransferTicketModalCustom';
import ShowTicketOpen from '../ShowTicketOpenModal';
import ShowTicketLogModal from '../ShowTicketLogModal';
import TicketMessagesDialog from '../TicketMessagesDialog';

import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import toastError from '../../toasts/toastError';
import { AuthContext } from '../../context/Auth/AuthContext';
import { TicketsContext } from '../../context/Tickets/TicketsContext';
import { v4 as uuidv4 } from 'uuid';
import useCompanySettings from '../../hooks/useSettings/companySettings';
import { getStyled } from './styles';

const TicketActionButtonsCustom = ({ ticket }) => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const { setCurrentTicket, setTab, setTabOpen, setSearchContact } =
    useContext(TicketsContext);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [showTicketLogOpen, setShowTicketLogOpen] = useState(false);
  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const { get: getSetting } = useCompanySettings();

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState('');
  const [queueTicketOpen, setQueueTicketOpen] = useState('');

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setOpenTicketMessageDialog(false);
      setShowTicketLogOpen(false);
    };
    fetchData();
  }, [ticket.id]);

  const handleCloseAlert = () => {
    setOpenAlert(false);
    setLoading(false);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenTransferModal = () => {
    setTransferTicketModalOpen(true);
  };

  const handleOpenConfirmationModal = () => {
    setConfirmationOpen(true);
  };

  const handleExportPDF = async () => {
    setOpenTicketMessageDialog(true);
    handleCloseMenu();
  };

  const handleShowLogTicket = async () => {
    setShowTicketLogOpen(true);
  };

  /* const handleContactToggleDisableBot = async () => {
    const { id } = ticket.contact;

    try {
      const { data } = await api.put(`/contacts/toggleDisableBot/${id}`);
      setDisableBot(data.disableBot);
    } catch (err) {
      toastError(err);
    }
  }; */

  const handleCloseTransferTicketModal = () => {
    setTransferTicketModalOpen(false);
  };

  const handleDeleteTicket = async () => {
    try {
      await api.delete(`/tickets/${ticket.id}`);
      navigate('/tickets');
    } catch (err) {
      toastError(err);
    }
  };

  const handleResolveTicket = async (e, status, userId, queueId) => {
    setLoading(true);

    try {
      await api.put(`/tickets/${ticket.id}`, {
        status: status,
        userId: userId,
        queueId: queueId,
        whatsappId: ticket.whatsappId,
        sendFarewellMessage: true,
      });

      await api.post(`/messages/${ticket.id}`, {
        isPrivate: 'true',
        body: `✔️ Ticket finalizado por: *${user.name}*`,
        read: true,
        fromMe: true,
      });
    } catch (err) {
      toastError(err);
    }

    setLoading(false);
  };

  const handleSendMessage = async (id, farewellMsg) => {
    if (!farewellMsg || farewellMsg.trim() === '') return;

    try {
      const message = {
        read: 1,
        fromMe: true,
        mediaUrl: '',
        body: farewellMsg.trim(),
      };

      await api.post(`/messages/${id}`, message);
    } catch (err) {
      toastError(err);
    }
  };

  const handleUpdateTicketStatus = async (
    e,
    status,
    userId,
    queueId,
    Queue,
  ) => {
    const userQueueId = Queue === 'NoQueue' ? user?.queues[0].id : queueId;

    try {
      setLoading(true);
      const data = {
        status: status,
        userId: Queue === 'returnQueue' ? null : userId,
        queueId: userQueueId,
      };

      const newTicket = await api.put(`/tickets/${ticket.id}`, data);

      if (status === 'canceled') {
        await api.post(`/messages/${ticket.id}`, {
          isPrivate: 'true',
          body: `❌ Ticket cancelado por: *${user.name}*`,
          read: true,
          fromMe: true,
        });
      }

      if (status === 'open' || status === 'group') {
        setCurrentTicket({ ...newTicket, code: '#' + status });
        navigate('/tickets');
        navigate(`/tickets/${newTicket.uuid}`);
        setTab(status);
        setTabOpen(status);
      } else {
        setCurrentTicket({ id: null, code: null });
        navigate('/tickets');
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleAcepptTicket = async (ticket) => {
    setLoading(true);

    try {
      const otherTicket = await api.put(`/tickets/${ticket.id}`, {
        status:
          ticket.isGroup && ticket.channel === 'whatsapp' ? 'group' : 'open',
        userId: user?.id,
        queueId: ticket.queueId ? ticket.queueId : user.queues[0].id,
      });

      if (otherTicket.data.id !== ticket.id) {
        if (otherTicket.data.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(otherTicket.data.user.name);
          setQueueTicketOpen(otherTicket.data.queue.name);
        } else {
          setLoading(false);
          setTab(ticket.isGroup ? 'group' : 'open');
          setTabOpen(ticket.isGroup ? 'group' : 'open');
          handleSelectTicket(otherTicket.data);
          navigate(`/tickets/${otherTicket.data.uuid}`);
        }
      } else {
        let setting;

        try {
          setting = await getSetting({
            column: 'sendGreetingAccepted',
          });
        } catch (err) {
          toastError(err);
        }

        if (
          setting?.sendGreetingAccepted === 'enabled' &&
          (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === 'enabled')
        ) {
          await handleSendMessage(ticket.id);
        }

        setTab(ticket.isGroup ? 'group' : 'open');
        setTabOpen(ticket.isGroup ? 'group' : 'open');
        handleSelectTicket(ticket);
        navigate(`/tickets/${ticket.uuid}`);
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  const handleNewTicket = async (ticket) => {
    setLoading(true);

    const data = {
      contactId: ticket.contactId,
      status: 'open',
      userId: user.id,
      queueId: ticket.queueId ? ticket.queueId : user.queues[0].id,
      whatsappId: ticket.whatsappId,
    };

    const { data: newTicket } = await api.post(`/tickets`, data);

    let setting;

    try {
      setting = await getSetting({
        column: 'sendGreetingAccepted',
      });
    } catch (err) {
      toastError(err);
    }

    if (
      setting?.sendGreetingAccepted === 'enabled' &&
      (!newTicket.isGroup || newTicket.whatsapp?.groupAsTicket === 'enabled')
    ) {
      handleSendMessage(newTicket.id);
    }

    setLoading(false);

    setTab(newTicket.isGroup ? 'group' : 'open');
    setTabOpen(newTicket.isGroup ? 'group' : 'open');
    setSearchContact('');

    handleSelectTicket(newTicket);

    navigate(`/tickets/${newTicket.uuid}`);
  };

  return (
    <>
      {openAlert && (
        <ShowTicketOpen
          isOpen={openAlert}
          handleClose={handleCloseAlert}
          user={userTicketOpen}
          queue={queueTicketOpen}
        />
      )}
      {showTicketLogOpen && (
        <ShowTicketLogModal
          isOpen={showTicketLogOpen}
          handleClose={() => setShowTicketLogOpen(false)}
          ticketId={ticket.id}
        />
      )}
      {openTicketMessageDialog && (
        <TicketMessagesDialog
          open={openTicketMessageDialog}
          handleClose={() => setOpenTicketMessageDialog(false)}
          ticketId={ticket.id}
        />
      )}
      <Box style={classes.actionButtons}>
        {ticket.status === 'spying' && (
          <Box style={{ ...classes.actionButtons, position: 'relative' }}>
            <ButtonWithSpinner
              sx={{
                ...theme.custom.confirmButton,
                minWidth: 'fit-content',
              }}
              size='small'
              variant='outlined'
              onClick={() => handleNewTicket(ticket)}
              disabled={loading}
            >
              {i18n.t('Abrir este Ticket')}
            </ButtonWithSpinner>

            <ButtonWithSpinner
              sx={{
                ...theme.custom.cancelButton,
                minWidth: 'fit-content',
              }}
              size='small'
              variant='outlined'
              onClick={() => handleDeleteTicket()}
              disabled={loading}
            >
              {i18n.t('Fechar Conversa')}
            </ButtonWithSpinner>
          </Box>
        )}
        {(ticket.status === 'closed' || ticket.status === 'canceled') &&
          (ticket.queueId === null || ticket.queueId === undefined) && (
            <ButtonWithSpinner
              disabled={loading}
              startIcon={<Replay />}
              size='small'
              onClick={() => handleAcepptTicket(ticket)}
              sx={{ color: theme.custom.colors.text }}
            >
              {i18n.t('messagesList.header.buttons.reopen')}
            </ButtonWithSpinner>
          )}
        {(ticket.status === 'closed' || ticket.status === 'canceled') &&
          ticket.queueId !== null && (
            <ButtonWithSpinner
              startIcon={<Replay />}
              disabled={loading}
              onClick={() => handleAcepptTicket(ticket)}
              sx={{ color: theme.custom.colors.text }}
            >
              {i18n.t('messagesList.header.buttons.reopen')}
            </ButtonWithSpinner>
          )}

        {(ticket.status === 'open' || ticket.status === 'group') && (
          <>
            <IconButton
              style={classes.bottomButtonVisibilityIcon}
              onClick={(e) =>
                handleResolveTicket(e, 'closed', user?.id, ticket?.queue?.id)
              }
              disabled={loading}
            >
              <Tooltip title={i18n.t('messagesList.header.buttons.resolve')}>
                <CheckCircle
                  style={{
                    color: !loading ? green[500] : grey[500],
                    fontSize: '40px',
                  }}
                />
              </Tooltip>
            </IconButton>

            <IconButton
              style={classes.bottomButtonVisibilityIcon}
              onClick={(e) =>
                handleUpdateTicketStatus(
                  e,
                  'pending',
                  user?.id,
                  ticket?.queue?.id,
                  'returnQueue',
                )
              }
              disabled={loading}
            >
              <Tooltip title={i18n.t('tickets.buttons.returnQueue')}>
                <ReplayCircleFilled
                  style={{
                    color: !loading ? grey[0] : grey[500],
                    fontSize: '40px',
                  }}
                />
              </Tooltip>
            </IconButton>

            <IconButton style={classes.bottomButtonVisibilityIcon}>
              <Tooltip title={i18n.t('tickets.buttons.transfer')}>
                <SwapHorizontalCircle
                  style={{
                    color: !loading ? orange[500] : grey[500],
                    fontSize: '40px',
                  }}
                  onClick={handleOpenTransferModal}
                />
              </Tooltip>
            </IconButton>

            <IconButton
              style={classes.bottomButtonVisibilityIcon}
              onClick={(e) =>
                handleUpdateTicketStatus(
                  e,
                  'canceled',
                  user?.id,
                  ticket?.queue?.id,
                )
              }
              disabled={loading}
            >
              <Tooltip title={i18n.t('tickets.buttons.cancel')}>
                <Cancel
                  style={{
                    color: !loading ? red[600] : grey[500],
                    fontSize: '40px',
                  }}
                />
              </Tooltip>
            </IconButton>

            {confirmationOpen && (
              <ConfirmationModal
                title={`${i18n.t(
                  'ticketOptionsMenu.confirmationModal.title',
                )} #${ticket.id}?`}
                open={confirmationOpen}
                onClose={setConfirmationOpen}
                onConfirm={handleDeleteTicket}
              >
                {i18n.t('ticketOptionsMenu.confirmationModal.message')}
              </ConfirmationModal>
            )}
            {transferTicketModalOpen && (
              <TransferTicketModalCustom
                modalOpen={transferTicketModalOpen}
                onClose={handleCloseTransferTicketModal}
                ticketid={ticket.id}
                ticket={ticket}
              />
            )}
          </>
        )}
        {ticket.status === 'pending' &&
          (ticket.queueId === null || ticket.queueId === undefined) && (
            <Box style={classes.actionButtons}>
              <ButtonWithSpinner
                size='small'
                variant='outlined'
                sx={theme.custom.confirmButton}
                onClick={() => handleAcepptTicket(ticket)}
                disabled={loading}
              >
                {i18n.t('messagesList.header.buttons.accept')}
              </ButtonWithSpinner>

              <ButtonWithSpinner
                size='small'
                variant='outlined'
                sx={theme.custom.cancelButton}
                onClick={(e) =>
                  handleUpdateTicketStatus(
                    e,
                    'canceled',
                    user?.id,
                    ticket?.queue?.id,
                  )
                }
                disabled={loading}
              >
                {i18n.t('messagesList.header.buttons.cancel')}
              </ButtonWithSpinner>
            </Box>
          )}
        {ticket.status === 'pending' && ticket.queueId !== null && (
          <Box style={classes.actionButtons}>
            <ButtonWithSpinner
              size='small'
              variant='outlined'
              sx={theme.custom.confirmButton}
              onClick={() => handleAcepptTicket(ticket)}
              disabled={loading}
            >
              {i18n.t('messagesList.header.buttons.accept')}
            </ButtonWithSpinner>
            <ButtonWithSpinner
              size='small'
              variant='outlined'
              sx={theme.custom.cancelButton}
              onClick={(e) =>
                handleUpdateTicketStatus(
                  e,
                  'canceled',
                  user?.id,
                  ticket?.queue?.id,
                )
              }
              disabled={loading}
            >
              {i18n.t('messagesList.header.buttons.cancel')}
            </ButtonWithSpinner>
          </Box>
        )}
        <IconButton
          aria-label='account of current user'
          aria-controls='menu-appbar'
          aria-haspopup='true'
          onClick={handleMenu}
          color='inherit'
          sx={{
            padding: '6 px',
            marginRight: 1,
            '&:hover': {
              backgroundColor: `rgb(221, 221, 221, ${theme.palette.mode === 'light' ? '0.7' : '0.2'})`,
            },
          }}
        >
          <MoreVert sx={{ fontSize: 20, color: theme.custom.colors.text }} />
        </IconButton>
        <Menu
          id='menu-appbar'
          anchorEl={anchorEl}
          getcontentanchorel={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={menuOpen}
          onClose={handleCloseMenu}
        >
          <MenuItem onClick={handleOpenConfirmationModal}>
            <Can
              role={user.profile}
              perform='ticket-options:deleteTicket'
              yes={() => i18n.t('tickets.buttons.deleteTicket')}
            />
          </MenuItem>
          <MenuItem onClick={handleShowLogTicket}>
            {i18n.t('messagesList.header.buttons.logTicket')}
          </MenuItem>
          <MenuItem onClick={handleExportPDF}>
            {i18n.t('ticketsList.buttons.exportAsPDF')}
          </MenuItem>
        </Menu>
      </Box>
    </>
  );
};

export default TicketActionButtonsCustom;
